import styles from "./DetailProductCard.module.css"
import React, { useRef, useState } from 'react';
import { Calendar, Users } from 'lucide-react';
import {ProductCardProps } from "../../../types/product.types"
import { getAllProductImages } from "../../../services/productImageService";
import CartButtonSecondary from "../../Button/CartButton/CartButtonSecondary";
import CartAmountSecondary from "../../Button/CartButton/CartAmountSecondary";
import Alergen from "../../Cards/AlergenCard/AlergenCard";
import CloseButton from "../../Button/CloseButton/CloseButton";
import useTimeModalActions from "../../../hooks/useTimeModalActions";
import { useCartContext } from "../../../context/CartContext";
import { useDetailContext } from '../../../context/DetailContext';
import { useWindowSize } from "../../../hooks/useWindowSize";
import { useOrderContext } from "../../../context/OrderContext";
import { usePreferences } from "../../../context/PreferencesContext";
import { czk_eur_rate } from "../../../constants/prices";
import { czk_to_eur, formatCurrency } from "../../../utils/prices";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { getPeopleWord } from "../../../utils/people";
import { normalizeDate, denormalizeDate, getDayWord } from "../../../utils/calendar";
import { defaultOrderTime } from "../../../constants/times";
import Tooltip from "../../Tooltip/Tooltip";
import { usePublicCatalog } from "../../../context/PublicCatalogContext";
import useCartAmountSync from "../../../hooks/useCartAmountSync";
import { getAdvanceDays, getDeliveryWindow } from "../../../utils/products";
import { getQuantityTooltip } from "../../../utils/quantity";
import ImageCarousel from "../../ImageCarousel/ImageCarousel";
import GalleryCard from "../../Cards/GalleryCard/GalleryCard";
interface DetailProductCardProps extends ProductCardProps{
    time?: string,
    date?: string
}

/**
* Detail card for products
*
* @param props ProductCardProps
* @param {Product} props.product - Product
* @returns TSX.Element
*/
const DetailProductCard = ({ product, type = "sales", time, date }: DetailProductCardProps) => {
    // Cart
    const { cartData, reloadCart, cartLoaded } = useCartContext();
    const { t, i18n } = useTranslation(); // Translation
    const { orderData } = useOrderContext(); // Order data
    const {closeDetail} = useDetailContext(); // Detail context
    const { openTimeModal } = useTimeModalActions(); // Time modal
    const screenWidth = useWindowSize(); // Window width
    const { selectedCurrency } = usePreferences(); // Current selected currency
    const effectiveDate = date ?? orderData.selectedDate; //"2025-09-08" (ISO)
    const dateKey = denormalizeDate(effectiveDate); // "8. 9. 2025"  -> matches cartData.items keys
    const dateISO = normalizeDate(effectiveDate); // "2025-09-08"  -> what cartChange expects 
    const {isPublicView} = usePublicCatalog();

    const { cartAmount, handleCart } = useCartAmountSync({
        cartData,
        cartLoaded,
        reloadCart,
        itemKey: {
            id: product.id,
            category: type,
            dateKey,
            dateISO,
            time,
        },
    });

    const advance = getAdvanceDays(product);
    const orderAdvanceLabel =
        i18n.language === "en" ? advance : product.order_in_advance ?? "N/A";
    // Sample time limit
    const { min: minDelivery, max: maxDelivery } = getDeliveryWindow(product);
    // Wording for days in advance
    const dayWord = getDayWord(i18n.language, advance);
    // Wording for recommended amount of people
    const peopleWord = getPeopleWord(i18n.language, product.max_participants ?? product.min_participants ?? 1)
    
    // Make sure price is always in correct currency
    const no_dph_price = product.pricing?.price_without_tax != null ? czk_to_eur(product.pricing.price_without_tax, czk_eur_rate, selectedCurrency).toFixed(2) : "N/A"
    const dph_price = product.pricing?.price_with_tax != null ? czk_to_eur(product.pricing.price_with_tax, czk_eur_rate, selectedCurrency).toFixed(2) : "N/A"
    
    const minOrder = product.min_order ?? 1;
    const granularity = product.granularity ?? 1;
    const tooltip = getQuantityTooltip({
        cartAmount,
        minOrder,
        granularity,
        language: i18n.language,
        itemType: "product",
    });
    const ref = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const images = getAllProductImages(product);

    return(
        <>
            <div className={styles.ProductDetail} key={product.id} >
                <div className={styles.ProductDetailBlur} onClick={closeDetail}></div>
                <div className={styles.ProductDetailCard}>
                    <div className={styles.ProductMain}>
                        {images.length > 1 ? (
                            <ImageCarousel images={images} className={styles.ProductImage} onPopup={true} />
                        ) : (
                            <GalleryCard imgSrc={images[0]} className={styles.ProductImage} onPopup={true} />
                        )}
                        <div className={styles.ProductMainText}>
                            <div className={styles.ProductMainInfo}>
                                <p className={styles.ProductTitle}>{i18n.language === "en" ? product.name_en || "N/A" : product.name}</p>
                                <div className={styles.ProductSpecs}>
                                    <div className={styles.ProductSpec}>
                                        <Calendar/>
                                        <p>{t("products.order")} {orderAdvanceLabel} {dayWord} {t("products.before")}</p>
                                    </div>
                                    <div className={styles.ProductSpec}>
                                        <Users/>
                                        <p>
                                        {t("products.good_for")} {((product.min_participants !== null && product.min_participants !== 0) ? product.min_participants : "1 ")}
                                        {product.max_participants !== null && product.max_participants !== product.min_participants
                                            ? ` - ${product.max_participants} `
                                            : " "}
                                        {(product.min_participants === 0 && (product.max_participants === 0 || product.max_participants === null)) ? getPeopleWord(i18n.language, 1) : peopleWord}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.ProductMainActions} >
                                <div className={styles.ProductPrices}>
                                        {product.pricing?.price_without_tax != null && (
                                            <p className={styles.NoDphPrice}>{formatCurrency(no_dph_price, selectedCurrency)} <Trans i18nKey="products.no_dph" components={{ 1: <span/> }} /></p>
                                        )}
                                        {product.pricing?.price_with_tax != null && (
                                            <p className={styles.DphPrice}>{formatCurrency(dph_price, selectedCurrency)} {t("products.dph")}</p>
                                        )}
                                </div>
                                {(cartAmount>0 && !isPublicView) ? (
                                    < div className = {styles.Amount} ref={ref} onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} >
                                        <CartAmountSecondary amountInCart={cartAmount} 
                                            disabledIncrease={cartAmount >= ((product.max_order === null || product.max_order === 0) ? 100 : product.max_order)}
                                            onIncrease={()=>handleCart(cartAmount+(1*(product.granularity??1)))}
                                            onDecrease={()=>{cartAmount > product.min_order ? handleCart(cartAmount-(1*(product.granularity??1))) : handleCart(0)}}
                                            className={styles.CartAmount} disabled={isPublicView} />
                                    {showTooltip && ref.current && <Tooltip desc={tooltip} className={styles.Tooltips} targetRef={ref}>{tooltip}</Tooltip>}
                                    </div>
                                ) : (
                                    <CartButtonSecondary
                                        text={t("buttons.order")}
                                        onClick={() =>
                                            openTimeModal(
                                                {
                                                    type: "single",
                                                    date: orderData.selectedDate,
                                                    time: defaultOrderTime,
                                                    productId: product.id,
                                                    prodType: type,
                                                    static: true,
                                                    amount: Math.max(product.min_order, product.granularity) ?? 1,
                                                    restrictions: { min: minDelivery, max: maxDelivery },
                                                    advance: advance,
                                                },
                                                { openOnTop: true }
                                            )
                                        }
                                        className={styles.DetailCartButton}
                                        disabled={isPublicView}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    {(() => {
                        const note = i18n.language === "en" ? product.note_en : product.note;
                        if (!note) return null;
                        return (
                            <div className={styles.ProductInfo}>
                                <p className={styles.InfoTitle}>{t("products.desc")}</p>
                                <p className={styles.InfoDescription}>
                                    {/*Format the note */}
                                    {note.split('\n').map((line, index) => (
                                        <React.Fragment key={index}>
                                            {line}
                                            <br />
                                        </React.Fragment>
                                    ))}
                                </p>
                            </div>
                        );
                    })()}
                    <div className={styles.ProductAlergens}>
                        {screenWidth > 900 ? (
                            <>
                            { product.allergens  && Object.entries(product.allergens)
                                .map(([alergen, val], index) => {
                                    if(val){
                                        return(
                                            <Alergen
                                            key={index+alergen}
                                            title={alergen}
                                            desc={t(`allergens.allergens.${index+1}`)}
                                            />
                                        )
                                    }
                                }
                            )}
                            </>
                        ):(
                            <>
                            {product.allergens && Object.entries(product.allergens)
                                .map(([alergen, val], index) => {
                                    if(val){
                                        return(
                                            <div className={styles.Alergen} key={index+alergen}>
                                                <Alergen
                                                title={alergen}
                                                />
                                                <p className={styles.AlergenTitle}>{t(`allergens.allergens.${index+1}`)}</p>
                                            </div>
                                        )
                                    }
                                }
                            )}
                            </>
                        )}
                        
                    </div>
                    <CloseButton onClick={()=>closeDetail && closeDetail()} className={styles.Close}/>
                </div>
            </div>
        </>
    )
};

export default DetailProductCard;
